import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_BabyFaceGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/baby-face-generator',
			name: 'Baby Face Generator — See What Your Child Could Look Like',
			description:
				"Upload two parent photos and see a realistic AI portrait of your future child. Choose gender, adjust age, and regenerate instantly. Try AI Lift's baby face generator now.",
			url: 'https://liftapp.ai/baby-face-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/baby-face-generator#product' },
				{ '@id': 'https://liftapp.ai/baby-face-generator#howto' },
				{ '@id': 'https://liftapp.ai/baby-face-generator#benefits' },
				{ '@id': 'https://liftapp.ai/baby-face-generator#faq' },
				{ '@id': 'https://liftapp.ai/baby-face-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/baby-face-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/baby-face-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/baby-face-generator#product',
			name: 'Baby Face Generator — AI Portrait Preview by Lift',
			description:
				"Upload two parent photos and see a realistic AI portrait of your future child. Choose gender, adjust age, and regenerate instantly. Try AI Lift's baby face generator now.",
			url: 'https://liftapp.ai/baby-face-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/baby-face-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your images',
					text: 'Choose two clear photos from your device to start your baby prediction',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Generate your result',
					text: 'Lift creates a lifelike child image from both photos using AI',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Adjust the settings',
					text: 'Switch gender and age options, replace photos, or generate again',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Save your result or share it with friends on social media',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/baby-face-generator#benefits',
			name: 'Who can benefit from the AI Baby Face Generator?',
			description:
				"Lift's Baby Face Generator is useful for couples, social media users, friends, gift seekers, casual AI explorers, and mobile users who want to preview a realistic AI-generated child portrait from two photos.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Couples and partners can see a realistic AI portrait of what their child could look like, with gender and age options to personalize the result',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Social media users can generate a visually striking child portrait and share it instantly with polished and scroll-stopping results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Friends and gift seekers can create a fun, personalized child prediction for someone special as an entertaining and memorable reveal',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Casual AI explorers can upload two photos, tap generate, and see a result in seconds with no prior experience required',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Mobile users can use the baby face generator fast and intuitively on any device with a single tap to swap photos and regenerate',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI baby face generator with gender selection — realistic child preview from parent photos',
				description:
					'Interface of baby face generator showing two parent photos, gender selection options, and resulting AI-generated baby portrait.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/baby-face-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Baby Face Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift Baby Face Generator is an entertainment feature that produces a realistic AI portrait showing what a future child could look like, based on two uploaded parent photos. It is built for fun and visual exploration — not medical or genetic prediction.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Baby Face Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload one photo of each parent and tap Generate Baby. Lift AI processes both images and creates a detailed child portrait. You can switch between boy, girl, or prediction results, and select an age stage — baby, child, or teen. Replace either photo and generate a new result at any time.',
					},
				},
				{
					'@type': 'Question',
					name: "Can I choose the child's gender and age?",
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Baby Face Generator lets you generate results for a boy, girl, or prediction, and preview different age stages such as baby, child, and teen.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/baby-face-generator#why-lift',
			name: "Why Lift's Baby Face Generator stands out",
			description:
				"Lift's Baby Face Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'AI baby face generator results shared on social media — viral child portrait preview',
				description:
					'Social media post showing AI-generated baby created from two parent photos with reactions and engagement icons using Lift baby face generator.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/baby-face-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/baby-face-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Baby Face Generator',
					item: 'https://liftapp.ai/baby-face-generator',
				},
			],
		},
	],
};
